import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { SUPPLIER_CATEGORIES } from "@/lib/constants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus } from "lucide-react";

function CreatableCombo({ value, onChange, options, placeholder, testid, noun }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const q = query.trim();
  const exists = options.some((o) => o.toLowerCase() === q.toLowerCase());
  const pick = (v) => { onChange(v); setOpen(false); setQuery(""); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 text-left text-sm" data-testid={testid}>
          <span className={`truncate ${value ? "text-slate-800" : "text-muted-foreground"}`}>{value || placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[220px] p-0" align="start">
        <Command>
          <CommandInput value={query} onValueChange={setQuery} placeholder={`Search Or Add ${noun}…`} data-testid={`${testid}-search`} />
          <CommandList>
            {!q && !options.length && <CommandEmpty>No {noun}s Yet</CommandEmpty>}
            {q && !exists && (
              <CommandGroup>
                <CommandItem value={`__add__${q}`} onSelect={() => pick(q.replace(/\b\w/g, (c) => c.toUpperCase()))} data-testid={`${testid}-add`}>
                  <Plus className="mr-2 h-4 w-4 text-primary" />Add New {noun} "{q}"
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {value && <CommandItem value="__clear__" onSelect={() => pick("")} className="text-muted-foreground">Clear</CommandItem>}
              {options.map((o) => (
                <CommandItem key={o} value={o} onSelect={() => pick(o)} data-testid={`${testid}-opt`}>
                  <Check className={`mr-2 h-4 w-4 ${value === o ? "opacity-100" : "opacity-0"}`} />{o}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const uniq = (arr) => [...new Map(arr.filter(Boolean).map((v) => [v.toLowerCase(), v])).values()].sort((a, b) => a.localeCompare(b));

export function CategoryCombo({ value, onChange, testid = "sku-category-combo" }) {
  const [extra, setExtra] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/supplier-categories").catch(() => ({ data: {} })), api.get("/catalog/categories").catch(() => ({ data: [] }))])
      .then(([a, b]) => setExtra([...(a.data.categories || []), ...(b.data || [])]));
  }, []);
  const known = Object.fromEntries(SUPPLIER_CATEGORIES.map((c) => [c.value, c.label]));
  const options = useMemo(() => uniq([...SUPPLIER_CATEGORIES.map((c) => c.label), ...extra.map((v) => known[v] || v)]), [extra]); // eslint-disable-line react-hooks/exhaustive-deps
  return <CreatableCombo value={value} onChange={onChange} options={options} placeholder="Select Category" testid={testid} noun="Category" />;
}

export function BrandCombo({ value, onChange, testid = "sku-brand-combo" }) {
  const [options, setOptions] = useState([]);
  useEffect(() => { api.get("/catalog/brands").then((r) => setOptions(uniq(r.data || []))).catch(() => {}); }, []);
  return <CreatableCombo value={value} onChange={onChange} options={options} placeholder="Select Brand / OEM" testid={testid} noun="Brand" />;
}

export function LinkBrandToggle({ brand, supplier, checked, onChange, testid = "sku-link-brand" }) {
  if (!brand || !supplier) return null;
  return (
    <label className="flex items-start gap-2 rounded-md border border-[#1B4383]/20 bg-[#F4F7FC] p-2.5 text-xs text-slate-700">
      <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span>Also Link <b>{brand}</b> To <b>{supplier.name}</b> As A Brand They Supply</span>
    </label>
  );
}

export const linkBrand = (supplierId, brand) => api.post(`/suppliers/${supplierId}/brands`, { brand });
